import { ReactElement, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import Button from '../controls/Button';
import SidePanelContainer from '../controls/SidePanelContainer';
import CustomInputBox from '../controls/CustomInputBox';
import authenticateService from '../services/authenticateService';
import { RHFValidations, TOAST_MESSAGES } from '../constants/global-constants';
import parsePasswordValidationMap from '../_helpers/passwordValidation';
import { useData } from '../context/data.context';
import { AwsLoginErrors } from '../constants/telosair-interfaces';
import { useAuth } from '../context/auth.context';
import PasswordInputBox from '../controls/PasswordInputBox';

type FORMDATA = {
  currentPassword: string;
  password: string;
  confirmPassword: string;
  email: string;
};

type AddUserModalProps = {
  handleClose: () => void;
  onForgetPass: () => void;
};

/**
 * Modal - ChangePasswordModal.
 * @param props - Properties of ChangePasswordModal.
 * @returns ReactElement.
 */
export default function ChangePasswordModal(props: AddUserModalProps): ReactElement {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { currentUser } = useData();
  const { logout } = useAuth();
  const username = currentUser?.userName;
  const { handleClose, onForgetPass } = props;
  const [loading, setLoading] = useState(false);
  const methods = useForm<FORMDATA>({
    mode: 'all',
    defaultValues: useMemo(() => {
      return {
        email: username
      };
    }, [username])
  });
  const { handleSubmit, watch, reset } = methods;

  useEffect(() => {
    reset({
      email: username
    });
  }, [username]);

  /**
   * Handles the Submit.
   * @param data -The list used to get data.
   */
  const onhandleSubmit = async (data: FORMDATA): Promise<void> => {
    if (!username) {
      toast.error(TOAST_MESSAGES.error);
      return;
    }
    try {
      setLoading(true);
      const { error } = await authenticateService.changePassword(
        data.currentPassword,
        data.password
      );
      if (!error) {
        toast.success('Password reset successful, please login again with your new password');
        setLoading(false);
        handleClose();
        if (logout) logout();
        return;
      }
      if (error === AwsLoginErrors.invalidCredentials) {
        toast.error("Current password isn't correct");
        setLoading(false);
        return;
      }
      if (error === AwsLoginErrors.notFound) {
        toast.error('User not found');
        setLoading(false);
        return;
      }
      toast.error(TOAST_MESSAGES.error);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      // eslint-disable-next-line no-console
      console.log(error);
    }
  };

  const confirmPasswordValidation = parsePasswordValidationMap(
    'confirm password',
    watch('password')
  );

  return (
    <SidePanelContainer closeModal={() => handleClose()}>
      <div className="pt-3 flex text-gray-500 dark:text-gray-300 text-lg">Change password</div>
      <div className="pb-3 flex text-gray-500 dark:text-gray-500 text-sm">
        Use this feature to change your password.
      </div>
      {/* eslint-disable-next-line */}
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onhandleSubmit)}>
          <CustomInputBox
            label="Email"
            idname="email"
            type="email"
            validationObject={RHFValidations.emailRequired}
            isDisabled
          />
          <PasswordInputBox
            label="Current Password"
            idname="currentPassword"
            validationObject={{ required: 'Current password is required' }}
          />
          <PasswordInputBox
            label="New Password"
            idname="password"
            autoComplete="off"
            validationObject={RHFValidations.password}
          />
          <PasswordInputBox
            label="Re-enter Password"
            idname="confirmPassword"
            autoComplete="off"
            validationObject={confirmPasswordValidation}
          />
          <button
            className="text-sm font-semibold text-primary-500 mt-6 underline"
            type="button"
            disabled
            onClick={() => onForgetPass()}
          >
            Forgot Password?
          </button>
          <div
            style={{ width: 'inherit' }}
            className="p-5 fixed bottom-0 right-0 flex justify-center gap-3 bg-white dark:bg-container-600"
          >
            <Button
              theme="secondary-color"
              size="sm"
              name="Discard"
              onClick={() => handleClose()}
              isBlockButton
            />
            <Button
              theme="primary"
              buttonType="submit"
              size="sm"
              loading={loading}
              name="Save Changes"
              isBlockButton
            />
          </div>
        </form>
      </FormProvider>
    </SidePanelContainer>
  );
}
